import React from 'react';
import { Link, useLocation, Outlet } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { cn } from '../lib/utils';
import { 
  LayoutDashboard, Target, FileText, Upload, Users, 
  Briefcase, Car, Settings, TestTube, LogOut 
} from 'lucide-react';

const navItems = [
  { path: '/', label: 'Dashboard', icon: LayoutDashboard },
  { path: '/threads', label: 'Threads', icon: Target },
  { path: '/evidence', label: 'Evidence', icon: FileText },
  { path: '/intake', label: 'Intake', icon: Upload },
  { path: '/funnels', label: 'Funnels', icon: Users },
  { path: '/deals', label: 'Deals', icon: Briefcase },
  { path: '/vehicles', label: 'Vehicles', icon: Car },
  { path: '/settings', label: 'Settings', icon: Settings },
  { path: '/tests', label: 'Tests', icon: TestTube },
];

const Layout = () => {
  const { user, logout } = useAuth();
  const location = useLocation();

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="border-b border-zinc-200 bg-white sticky top-0 z-50">
        <div className="max-w-[1600px] mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="font-manrope text-lg font-bold tracking-tight text-zinc-900">
                Dharmadhara Group Agent OS
                <span className="ml-2 text-xs font-normal text-zinc-400 font-inter">(MVP)</span>
              </h1>
              <p className="text-xs text-zinc-500 font-inter tracking-wide">
                Evidence • Execution • Strategy (Human-in-loop)
              </p>
            </div>
            <div className="flex items-center gap-4">
              <div className="text-right">
                <p className="text-sm font-medium font-inter text-zinc-900">{user?.name}</p>
                <p className="text-xs text-zinc-500 font-mono">{user?.role}</p>
              </div>
              <Button 
                variant="ghost" 
                size="icon" 
                onClick={logout}
                className="text-zinc-400 hover:text-zinc-900"
                data-testid="logout-btn"
              >
                <LogOut className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-[1600px] mx-auto flex">
        {/* Sidebar */}
        <aside className="w-56 border-r border-zinc-200 min-h-[calc(100vh-73px)] sticky top-[73px] bg-white">
          <nav className="p-4 space-y-1">
            {navItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path || 
                (item.path !== '/' && location.pathname.startsWith(item.path));
              
              return (
                <Link key={item.path} to={item.path}>
                  <div
                    className={cn(
                      'flex items-center gap-3 px-3 py-2 rounded-sm text-sm font-inter transition-colors',
                      isActive 
                        ? 'bg-zinc-900 text-white' 
                        : 'text-zinc-600 hover:bg-zinc-100 hover:text-zinc-900'
                    )}
                    data-testid={`nav-${item.label.toLowerCase()}`}
                  >
                    <Icon className="w-4 h-4" />
                    {item.label}
                  </div>
                </Link>
              );
            })}
          </nav>
        </aside>

        {/* Main Content */}
        <main className="flex-1 p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default Layout;
